// Task detail sections of decision D23: "Production" (GET /v1/tasks/{id}/production — time producing, waiting for a
// person and blocked, attempts, tokens, cost with the calls of unknown cost, models and agents) and "Council"
// (GET /v1/tasks/{id}/council-reviews — each review with its verdict, risk, reasons and the action applied).
import { useI18n } from "../../i18n/index.tsx";
import { getTaskProduction, listTaskCouncilReviews } from "../../api/endpoints-wave5.ts";
import { useAsync } from "../../lib/useAsync.ts";
import { formatNumber } from "../../lib/format.ts";
import { RISK_VIEW } from "../../lib/status.ts";
import { ErrorBanner, LoadingBlock } from "../../components/Feedback.tsx";
import { StatusPill } from "../../components/Status.tsx";
import { AgentsTable, CostText, ModelsTable, Stat, formatDuration, labelOr } from "./common.tsx";

export function TaskProductionSection({ taskId, version }: { taskId: string; version: number }) {
  const { t, locale } = useI18n();
  const prod = useAsync((signal) => getTaskProduction(taskId, signal), [taskId, version]);
  const p = prod.data;
  return (
    <section data-testid="task-production" aria-labelledby={`production-${taskId}`}>
      <h3 id={`production-${taskId}`} className="detail-heading">
        {t("production.title")}
      </h3>
      {prod.loading ? (
        <LoadingBlock label={t("common.loading")} lines={2} />
      ) : prod.error && !p ? (
        <ErrorBanner error={prod.error} onRetry={prod.reload} title={t("production.loadFailed")} />
      ) : p ? (
        <div className="stack">
          <div className="stat-grid">
            <Stat label={t("production.productionTime")} value={formatDuration(p.production_ms)} testId="production-time" />
            <Stat label={t("production.waitingHuman")} value={formatDuration(p.waiting_human_ms)} />
            <Stat label={t("production.blockedTime")} value={formatDuration(p.blocked_ms)} />
            <Stat label={t("production.attempts")} value={t("production.attemptsValue", { ok: p.attempts.succeeded, failed: p.attempts.failed })} />
            <Stat label={t("production.tokens")} value={t("production.tokensValue", { input: formatNumber(p.tokens.input, locale), output: formatNumber(p.tokens.output, locale) })} />
            <Stat label={t("production.cost")} value={<CostText cents={p.known_cost_cents} unknownCalls={p.unknown_cost_calls} />} />
          </div>
          <ModelsTable models={p.models} label={t("production.models")} />
          <AgentsTable agents={p.agents} label={t("production.agents")} />
        </div>
      ) : null}
    </section>
  );
}

export function CouncilReviewsSection({ taskId, version }: { taskId: string; version: number }) {
  const { t, formatDateTime } = useI18n();
  const reviews = useAsync((signal) => listTaskCouncilReviews(taskId, signal), [taskId, version]);
  const items = reviews.data?.items ?? [];
  return (
    <section data-testid="task-council" aria-labelledby={`council-${taskId}`}>
      <h3 id={`council-${taskId}`} className="detail-heading">
        {t("council.title")}
      </h3>
      {reviews.loading ? (
        <LoadingBlock label={t("common.loading")} lines={2} />
      ) : reviews.error && !reviews.data ? (
        <ErrorBanner error={reviews.error} onRetry={reviews.reload} title={t("council.loadFailed")} />
      ) : items.length === 0 ? (
        <p className="subtle">{t("council.none")}</p>
      ) : (
        <ul className="hit-list">
          {items.map((r) => (
            <li key={r.id} className="hit" data-testid="council-review" data-verdict={r.verdict}>
              <div className="hit-head">
                <strong>{labelOr(t, `council.verdict.${r.verdict}`, r.verdict)}</strong>
                {RISK_VIEW[r.risk] ? <StatusPill view={RISK_VIEW[r.risk]!} /> : <span className="badge">{r.risk}</span>}
                <span className="subtle">{formatDateTime(r.created_at)}</span>
              </div>
              {/* D41-C12 / E6.10: a model-mode review is one call speaking for five members, not five agents. */}
              {r.mode === "model" ? <p className="subtle" data-testid="council-single-call">{t("council.singleCall")}</p> : null}
              {r.reasons.length > 0 ? (
                <ul className="council-reasons">
                  {r.reasons.map((reason) => (
                    <li key={reason}>{reason}</li>
                  ))}
                </ul>
              ) : null}
              {r.applied_action ? <p className="subtle">{t("council.applied", { action: labelOr(t, `council.action.${r.applied_action}`, r.applied_action) })}</p> : null}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
